import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchSupportMessages } from '../../api/support_chat_api';
import { SUPPORT_CHAT_PAGE_SIZE, SUPPORT_CHAT_POLL_MS, SUPPORT_CHAT_URL_REFRESH_MS } from './constants';

function upsertById(prev, incoming) {
  if (!incoming?.length) return prev;
  const byId = new Map(prev.map((message) => [message.id, message]));
  incoming.forEach((message) => {
    byId.set(message.id, message);
  });
  return [...byId.values()].sort((a, b) => a.id - b.id);
}

function boundsOf(rows) {
  if (!rows.length) return null;
  const ids = rows.map((row) => row.id);
  return { oldest: Math.min(...ids), newest: Math.max(...ids) };
}

function rememberBounds(oldestRef, newestRef, rows) {
  const bounds = boundsOf(rows);
  if (!bounds) return;
  oldestRef.current = oldestRef.current == null ? bounds.oldest : Math.min(oldestRef.current, bounds.oldest);
  newestRef.current = Math.max(newestRef.current ?? 0, bounds.newest);
}

let pendingIdCounter = 0;
const nextPendingId = () => `pending-${Date.now()}-${++pendingIdCounter}`;

/**
 * История чата:
 * - открытие — последние 50 сообщений (`limit`);
 * - прокрутка вверх — более ранние (`before_id`);
 * - поллинг — только новые (`after_id`).
 *
 * Подписанные ссылки вложений живут около 10 минут, поэтому раз в несколько
 * минут видимое окно истории запрашивается заново и подменяется по id.
 * Поллинг останавливается, пока вкладка скрыта, и продолжается при возврате.
 *
 * Сообщения, которые пользователь только что отправил, показываются сразу
 * как pending — до подтверждения следующим запросом истории.
 */
export function useSupportChatMessages() {
  const [confirmedMessages, setConfirmedMessages] = useState([]);
  const [pendingMessages, setPendingMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(false);

  const lastMessageIdRef = useRef(null);
  const oldestMessageIdRef = useRef(null);
  const hasMoreOlderRef = useRef(false);
  const loadingOlderRef = useRef(false);
  const readyRef = useRef(false);
  const lastFullRefreshAtRef = useRef(0);
  const pollTimeoutRef = useRef(null);
  const pollIntervalRef = useRef(SUPPORT_CHAT_POLL_MS);

  const markHasMore = useCallback((value) => {
    hasMoreOlderRef.current = value;
    setHasMoreOlder(value);
  }, []);

  const pollNewMessages = useCallback(async () => {
    const afterId = lastMessageIdRef.current;
    const rows =
      afterId == null ? await fetchSupportMessages({ limit: SUPPORT_CHAT_PAGE_SIZE }) : await fetchSupportMessages({ afterId });

    if (rows.length) {
      setConfirmedMessages((prev) => upsertById(prev, rows));
      rememberBounds(oldestMessageIdRef, lastMessageIdRef, rows);
    }
  }, []);

  const refreshLoadedMessages = useCallback(async () => {
    const targetOldest = oldestMessageIdRef.current;
    let beforeId;
    const collected = [];

    for (let page = 0; page < 30; page += 1) {
      const rows = await fetchSupportMessages(
        beforeId == null ? { limit: SUPPORT_CHAT_PAGE_SIZE } : { beforeId, limit: SUPPORT_CHAT_PAGE_SIZE }
      );

      if (!rows.length) {
        markHasMore(false);
        break;
      }

      collected.push(...rows);
      const bounds = boundsOf(rows);

      if (rows.length < SUPPORT_CHAT_PAGE_SIZE) {
        markHasMore(false);
        break;
      }

      if (targetOldest == null || bounds.oldest <= targetOldest) break;
      beforeId = bounds.oldest;
    }

    if (collected.length) {
      setConfirmedMessages((prev) => upsertById(prev, collected));
      rememberBounds(oldestMessageIdRef, lastMessageIdRef, collected);
    }

    lastFullRefreshAtRef.current = Date.now();
  }, [markHasMore]);

  const loadLatest = useCallback(async () => {
    const rows = await fetchSupportMessages({ limit: SUPPORT_CHAT_PAGE_SIZE });
    setConfirmedMessages([...rows].sort((a, b) => a.id - b.id));
    oldestMessageIdRef.current = null;
    lastMessageIdRef.current = null;
    rememberBounds(oldestMessageIdRef, lastMessageIdRef, rows);
    markHasMore(rows.length >= SUPPORT_CHAT_PAGE_SIZE);
    lastFullRefreshAtRef.current = Date.now();
  }, [markHasMore]);

  const loadOlder = useCallback(async () => {
    const beforeId = oldestMessageIdRef.current;
    if (beforeId == null || !hasMoreOlderRef.current || loadingOlderRef.current) return;

    loadingOlderRef.current = true;
    setLoadingOlder(true);
    try {
      const rows = await fetchSupportMessages({ beforeId, limit: SUPPORT_CHAT_PAGE_SIZE });
      if (rows.length) {
        setConfirmedMessages((prev) => upsertById(prev, rows));
        rememberBounds(oldestMessageIdRef, lastMessageIdRef, rows);
      }
      if (rows.length < SUPPORT_CHAT_PAGE_SIZE) markHasMore(false);
    } catch (error) {
      console.error(error);
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  }, [markHasMore]);

  useEffect(() => {
    let cancelled = false;

    const schedulePoll = () => {
      if (cancelled) return;
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);

      pollTimeoutRef.current = setTimeout(async () => {
        if (cancelled) return;

        if (!document.hidden) {
          try {
            const isUrlRefreshDue = Date.now() - lastFullRefreshAtRef.current >= SUPPORT_CHAT_URL_REFRESH_MS;
            if (isUrlRefreshDue) {
              await refreshLoadedMessages();
            } else {
              await pollNewMessages();
            }
            pollIntervalRef.current = SUPPORT_CHAT_POLL_MS;
          } catch (error) {
            console.error(error);
            pollIntervalRef.current = Math.min(pollIntervalRef.current * 2, 60000);
          }
        }

        schedulePoll();
      }, pollIntervalRef.current);
    };

    loadLatest()
      .catch((error) => {
        console.error(error);
        pollIntervalRef.current = Math.min(pollIntervalRef.current * 2, 60000);
      })
      .finally(() => {
        if (cancelled) return;
        readyRef.current = true;
        setLoading(false);
        schedulePoll();
      });

    const onVisibilityChange = () => {
      if (document.hidden || cancelled || !readyRef.current) return;
      pollIntervalRef.current = SUPPORT_CHAT_POLL_MS;
      refreshLoadedMessages().catch((error) => console.error(error));
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, [loadLatest, pollNewMessages, refreshLoadedMessages]);

  const addPendingMessage = useCallback((message) => {
    const id = nextPendingId();
    setPendingMessages((prev) => [...prev, { ...message, id, pending: true }]);
    return id;
  }, []);

  const removePendingMessage = useCallback((id) => {
    setPendingMessages((prev) => prev.filter((message) => message.id !== id));
  }, []);

  const refetchMessages = useCallback(async () => {
    try {
      await pollNewMessages();
    } catch (error) {
      console.error(error);
    }
  }, [pollNewMessages]);

  const messages = useMemo(() => [...confirmedMessages, ...pendingMessages], [confirmedMessages, pendingMessages]);

  return {
    messages,
    loading,
    loadingOlder,
    hasMoreOlder,
    loadOlder,
    refetchMessages,
    addPendingMessage,
    removePendingMessage,
  };
}
